// 邀请 landing 页 —— /app/#/invite/:token
//
// 三态：loading / valid / invalid
//   valid:
//     - 已登录: 显示 issuer + room + "接受邀请" → POST redeem → 跳 Room
//     - 未登录: "请先登录" → 存 pending invite → 跳 /
//   invalid:
//     - 显示原因（expired / revoked / exhausted / not_found / issuer_gone）

import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import {
  InvitePreview,
  InviteApiError,
  previewInvite,
  redeemInvite,
  setPendingInvite,
} from "../lib/invite-api";
import { useAuth } from "../lib/use-auth";

type State =
  | { kind: "loading" }
  | { kind: "valid"; preview: Extract<InvitePreview, { valid: true }> }
  | { kind: "invalid"; reason: string };

export function InvitePage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { status, session } = useAuth();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ kind: "invalid", reason: "missing_token" });
      return;
    }
    previewInvite(token)
      .then((p) => {
        if (cancelled) return;
        if (p.valid) {
          setState({ kind: "valid", preview: p });
        } else {
          setState({ kind: "invalid", reason: p.reason });
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof InviteApiError ? e.serverMessage : String(e);
        setState({ kind: "invalid", reason: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Auth 状态变化后处理「等登录回来自动 redeem」
  useEffect(() => {
    // 已登录 + valid + 是 room kind: 自动 redeem 跳 Room
    // 是 use kind: 显示 "已接受" 跳首页
    // 这里不自动，由用户点按钮触发，避免突然跳转
  }, [status, session, state]);

  if (status === "loading") {
    return <Centered>正在确认登录状态…</Centered>;
  }

  if (state.kind === "loading") {
    return <Centered>正在校验邀请…</Centered>;
  }

  if (state.kind === "invalid") {
    return (
      <Centered>
        <h2 className="invite__title">邀请不可用</h2>
        <p className="invite__reason">{humanReason(state.reason)}</p>
        <a className="invite__btn invite__btn--muted" href="#/">
          回首页
        </a>
      </Centered>
    );
  }

  const p = state.preview;
  const isRoom = p.kind === "room";
  const issuer = p.issuer_name || "(匿名)";
  const roomName = p.room_name || p.room_id || "";

  async function onAccept() {
    if (!session) {
      // 把 token 暂存 → 登录后回来
      setPendingInvite(token);
      navigate("/");
      return;
    }
    setRedeeming(true);
    setError(null);
    try {
      const r = await redeemInvite(token, session);
      if (r.kind === "room" && r.room_id) {
        navigate(`/rooms/${encodeURIComponent(r.room_id)}`);
      } else {
        navigate("/rooms");
      }
    } catch (e: unknown) {
      const msg = e instanceof InviteApiError ? e.serverMessage : String(e);
      setError(msg);
      setRedeeming(false);
    }
  }

  // 自动 redirect: 邀请人邀请自己（理论上不该出现, 但保险）
  if (status === "authenticated" && false) {
    return <Navigate to="/rooms" replace />;
  }

  return (
    <Centered>
      <h2 className="invite__title">
        {issuer} 邀请你
        {isRoom ? (
          <>
            加入 <span className="invite__room">#{roomName}</span>
          </>
        ) : (
          "使用 ChatBox"
        )}
      </h2>
      <p className="invite__meta">
        {p.remaining_uses === -1
          ? "无限名额"
          : `剩余 ${p.remaining_uses} 个名额`}
        ＿{formatExpiry(p.expires_at)}
      </p>
      {error && <p className="invite__error">出错：{error}</p>}
      <button
        className="invite__btn"
        onClick={onAccept}
        disabled={redeeming}
      >
        {redeeming
          ? "处理中…"
          : status === "authenticated"
          ? "接受邀请"
          : "登录后接受"}
      </button>
      {status !== "authenticated" && (
        <p className="invite__hint">
          未登录? 注册或登录后会自动接受此邀请
        </p>
      )}
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="invite-page">
      <div className="invite__card">{children}</div>
    </div>
  );
}

function humanReason(r: string): string {
  switch (r) {
    case "not_found":
      return "邀请链接不存在或已被删除。";
    case "expired":
      return "邀请已过期。";
    case "revoked":
      return "邀请人已撤回此链接。";
    case "exhausted":
      return "邀请已用完名额。";
    case "issuer_gone":
      return "邀请人账号已不存在。";
    case "missing_token":
      return "链接缺少 token。";
    default:
      return r;
  }
}

function formatExpiry(ts: number): string {
  const days = Math.max(0, Math.round((ts - Date.now()) / 86_400_000));
  if (days === 0) {
    const hrs = Math.max(0, Math.round((ts - Date.now()) / 3_600_000));
    return `${hrs} 小时后过期`;
  }
  return `${days} 天后过期`;
}
